// 🧪 SSOT 테스트 서비스 - ID 정합성 검증 및 테스트 격리 기능
// [배경] SSOT 통합이 제대로 됐는지 검증하는 테스트 유틸리티
// [목적] 보안 제외, SSOT 정합성만 검증 (Non-canonical ID, Orphan, Duplicate 탐지)

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  isCanonicalModelId,
  extractPureModelId,
  normalizeStorage,
  normalizeConnectivity,
  getConnectivityForPriceId,
  DeviceType,
  DeviceBrand,
  ConnectivityType,
  DeviceCatalogDocument,
  UnifiedPriceDocument
} from '../types/deviceCatalog';
import { IDAliasService } from './idAliasService';
import { GradePriceInfo } from '../types/gradeSystem';

// ═══════════════════════════════════════════════════════════════════════════
// 🔑 타입 정의
// ═══════════════════════════════════════════════════════════════════════════

// 테스트 실행 정보
export interface TestRunContext {
  testRunId: string;
  startedAt: Date;
  isTestMode: true;
}

// 조회 단계 로그
export interface LookupStepLog {
  step: number;
  action: string;
  input: string;
  output: string | null;
  success: boolean;
  isFallback: boolean;  // 2단계 외 폴백 여부
  timestamp: Date;
}

// 스캔 결과 아이템
export interface NonCanonicalModelItem {
  id: string;
  suggestedCanonical: string;
  issues: string[];
  collection: string;
}

export interface OrphanPriceItem {
  priceId: string;
  modelId: string;
  reason: string;
}

export interface DuplicateVariantItem {
  modelId: string;
  storage: string;
  connectivity: string;
  priceIds: string[];
  count: number;
}

// 스캔 결과
export interface ScanResult {
  nonCanonicalModels: NonCanonicalModelItem[];
  orphanPrices: OrphanPriceItem[];
  duplicateVariants: DuplicateVariantItem[];
  totalModels: number;
  totalPrices: number;
  scannedAt: Date;
}

// Workflow 테스트 결과
export interface WorkflowTestResult {
  testName: string;
  passed: boolean;
  expectedId: string;
  actualId: string | null;
  details: Record<string, any>;
  lookupSteps: LookupStepLog[];
  error?: string;
}

// Playground 정규화 결과
export interface NormalizeResult {
  inputStorage: string;
  inputConnectivity: string;
  normalizedStorage: string | null;
  normalizedConnectivity: string | null;
  expectedPriceIdSuffix: string;
  deviceType: DeviceType;
}

// 자동 채점 결과
export interface ScorecardResult {
  passed: boolean;
  nonCanonicalCount: number;
  orphanCount: number;
  duplicateCount: number;
  idMatchRate: number;
  noFallbackViolations: boolean;
  details: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔑 테스트 컬렉션 상수
// ═══════════════════════════════════════════════════════════════════════════

const TEST_COLLECTIONS = {
  MODEL: 'device_catalog__ssot_test',
  PRICE: 'unified_prices__ssot_test'
} as const;

const PRODUCTION_COLLECTIONS = {
  MODEL: 'device_catalog',
  PRICE: 'unified_prices'
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// 🔑 SSOT 테스트 서비스
// ═══════════════════════════════════════════════════════════════════════════

export class SSOTTestService {
  // 현재 테스트 컨텍스트
  private static currentTestRun: TestRunContext | null = null;
  
  // 조회 단계 로그 수집기
  private static lookupLogs: LookupStepLog[] = [];
  
  // ========================================
  // 🔑 테스트 세션 관리
  // ========================================
  
  /**
   * 🆕 새 테스트 실행 시작
   */
  static startTestRun(): TestRunContext {
    const testRunId = `test-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    this.currentTestRun = {
      testRunId,
      startedAt: new Date(),
      isTestMode: true
    };
    this.lookupLogs = [];
    console.log(`🧪 테스트 실행 시작: ${testRunId}`);
    return this.currentTestRun;
  }
  
  /**
   * 현재 테스트 컨텍스트 조회
   */
  static getCurrentTestRun(): TestRunContext | null {
    return this.currentTestRun;
  }
  
  /**
   * 테스트 실행 종료
   */
  static endTestRun(): void {
    if (this.currentTestRun) {
      console.log(`🧪 테스트 실행 종료: ${this.currentTestRun.testRunId}`);
    }
    this.currentTestRun = null;
    this.lookupLogs = [];
  }
  
  // ========================================
  // 🔑 조회 단계 로그
  // ========================================
  
  /**
   * 조회 단계 로그 추가
   */
  static addLookupLog(log: Omit<LookupStepLog, 'timestamp'>): void {
    this.lookupLogs.push({
      ...log,
      timestamp: new Date()
    });
  }
  
  /**
   * 조회 단계 로그 조회 및 클리어
   */
  static getAndClearLookupLogs(): LookupStepLog[] {
    const logs = [...this.lookupLogs];
    this.lookupLogs = [];
    return logs;
  }
  
  /**
   * 폴백 위반 여부 확인 (2단계 외 조회 발생)
   */
  static hasFallbackViolation(logs: LookupStepLog[]): boolean {
    return logs.some(log => log.isFallback);
  }
  
  // ========================================
  // 🔑 SSOT 유틸 래퍼 (검증용)
  // ========================================
  
  /**
   * 🔑 Canonical Model ID 생성 (SSOT)
   */
  static createCanonicalModelId(
    brand: DeviceBrand,
    deviceType: DeviceType,
    series: string,
    modelEn: string
  ): string {
    return IDAliasService.createCanonicalModelId({
      brand,
      deviceType,
      series,
      modelEn
    });
  }
  
  /**
   * 🔑 Canonical Price ID 생성 (SSOT)
   */
  static createCanonicalPriceId(
    modelId: string,
    storage: string,
    connectivity?: string
  ): string {
    return IDAliasService.createCanonicalPriceId(
      modelId,
      { storage, connectivity }
    );
  }
  
  /**
   * 🔑 Storage 정규화 (SSOT)
   */
  static normalizeStorage(input: string, deviceType?: DeviceType): string | null {
    return normalizeStorage(input, deviceType);
  }
  
  /**
   * 🔑 Connectivity 정규화 (SSOT)
   */
  static normalizeConnectivity(input: string | undefined): 'wifi' | 'cellular' | null {
    return normalizeConnectivity(input);
  }
  
  /**
   * 🔑 Model ID 해석 (Alias 기반)
   */
  static async resolveModelId(anyId: string): Promise<string> {
    const step1Input = anyId;
    
    // 1단계: Canonical 확인
    if (isCanonicalModelId(anyId)) {
      this.addLookupLog({
        step: 1,
        action: 'canonical_check',
        input: step1Input,
        output: anyId,
        success: true,
        isFallback: false
      });
      return anyId;
    }
    
    // 2단계: Alias Resolve
    const resolved = await IDAliasService.resolveModelId(anyId);
    this.addLookupLog({
      step: 2,
      action: 'alias_resolve',
      input: step1Input,
      output: resolved,
      success: resolved !== anyId,
      isFallback: false
    });
    
    return resolved;
  }
  
  // ========================================
  // 🔑 가격 조회 (2단계 검증)
  // ========================================
  
  /**
   * 🔑 가격 조회 (2단계만 허용)
   * 1단계: Canonical priceId로 직접 조회
   * 2단계: Alias resolve 후 canonical 재조회
   * → 부분문자열/키워드 등 폴백 발생 시 FAIL
   */
  static async lookupPriceStrict(
    modelId: string,
    storage: string,
    connectivity?: string,
    useTestCollection: boolean = false
  ): Promise<{ price: GradePriceInfo | null; logs: LookupStepLog[]; hasFallback: boolean }> {
    this.lookupLogs = [];
    
    const collectionName = useTestCollection ? TEST_COLLECTIONS.PRICE : PRODUCTION_COLLECTIONS.PRICE;
    
    // modelId 정규화
    const resolvedModelId = await this.resolveModelId(modelId);
    
    // 1단계: Canonical priceId로 직접 조회
    const canonicalPriceId = this.createCanonicalPriceId(resolvedModelId, storage, connectivity);
    
    this.addLookupLog({
      step: 1,
      action: 'canonical_price_lookup',
      input: canonicalPriceId,
      output: null,
      success: false,
      isFallback: false
    });
    
    try {
      const docSnap = await getDoc(doc(db, collectionName, canonicalPriceId));
      
      if (docSnap.exists()) {
        const data = docSnap.data() as UnifiedPriceDocument;
        this.lookupLogs[this.lookupLogs.length - 1].output = canonicalPriceId;
        this.lookupLogs[this.lookupLogs.length - 1].success = true;
        
        return {
          price: data.gradePrices,
          logs: this.getAndClearLookupLogs(),
          hasFallback: false
        };
      }
    } catch (error) {
      console.error('1단계 조회 실패:', error);
    }
    
    // 2단계: Alias를 통한 재조회
    // (이미 resolveModelId에서 alias resolve 됨)
    // 여기서 추가 폴백이 발생하면 FAIL
    
    // 부분 문자열 매칭이나 키워드 검색은 금지
    // 2단계까지만 허용
    
    this.addLookupLog({
      step: 2,
      action: 'alias_price_lookup',
      input: `resolved: ${resolvedModelId}`,
      output: null,
      success: false,
      isFallback: false
    });
    
    // 최종: 2단계까지 찾지 못함
    const logs = this.getAndClearLookupLogs();
    
    return {
      price: null,
      logs,
      hasFallback: false  // 정상적으로 2단계까지만 시도
    };
  }
  
  // ========================================
  // 🔑 Scan 기능 (읽기 전용)
  // ========================================
  
  /**
   * 🔍 Non-canonical modelId 탐지
   */
  static async scanNonCanonicalModels(
    useTestCollection: boolean = false
  ): Promise<NonCanonicalModelItem[]> {
    const collectionName = useTestCollection ? TEST_COLLECTIONS.MODEL : PRODUCTION_COLLECTIONS.MODEL;
    const results: NonCanonicalModelItem[] = [];
    
    try {
      const snapshot = await getDocs(collection(db, collectionName));
      
      snapshot.docs.forEach(docSnap => {
        const modelId = docSnap.id;
        
        if (!isCanonicalModelId(modelId)) {
          const issues: string[] = [];
          
          // 오염 패턴 검사
          if (/-\d+(gb|tb)-/i.test(modelId)) issues.push('storage 포함');
          if (/-\d+$/.test(modelId) && !/\d+(gb|tb|inch|mm)$/i.test(modelId)) issues.push('index/순번 포함');
          if (/-wifi-|-cellular-/i.test(modelId)) issues.push('connectivity 포함');
          
          results.push({
            id: modelId,
            suggestedCanonical: extractPureModelId(modelId),
            issues,
            collection: collectionName
          });
        }
      });
      
      console.log(`🔍 Non-canonical modelId 탐지: ${results.length}개`);
    } catch (error) {
      console.error('Non-canonical 스캔 실패:', error);
    }
    
    return results;
  }
  
  /**
   * 🔍 Orphan price 탐지 (modelId가 device_catalog에 없음)
   * 
   * 🔑 개선: canonical ID 변환을 적용하여 레거시/canonical 혼재 상황에서도 정확히 탐지
   */
  static async scanOrphanPrices(
    useTestCollection: boolean = false
  ): Promise<OrphanPriceItem[]> {
    const modelCollection = useTestCollection ? TEST_COLLECTIONS.MODEL : PRODUCTION_COLLECTIONS.MODEL;
    const priceCollection = useTestCollection ? TEST_COLLECTIONS.PRICE : PRODUCTION_COLLECTIONS.PRICE;
    const results: OrphanPriceItem[] = [];
    
    try {
      // 🔑 개선: 원본 ID와 canonical ID 모두 수집
      const modelSnapshot = await getDocs(collection(db, modelCollection));
      const validModelIds = new Set<string>();
      const canonicalModelIds = new Set<string>();
      
      modelSnapshot.docs.forEach(d => {
        validModelIds.add(d.id);
        // canonical 변환된 ID도 추가
        const canonical = extractPureModelId(d.id);
        canonicalModelIds.add(canonical);
        // 원본이 canonical과 다르면 alias로도 등록
        if (d.id !== canonical) {
          validModelIds.add(canonical);
        }
      });
      
      // 가격 문서 스캔
      const priceSnapshot = await getDocs(collection(db, priceCollection));
      
      priceSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data() as UnifiedPriceDocument;
        const modelId = data.modelId;
        
        if (!modelId) {
          results.push({
            priceId: docSnap.id,
            modelId: '(없음)',
            reason: 'modelId 필드 없음'
          });
          return;
        }
        
        // 🔑 개선: 가격 문서의 modelId도 canonical로 변환해서 비교
        const priceModelIdCanonical = extractPureModelId(modelId);
        
        // 원본 ID, 정제된 ID, canonical ID 모두로 확인
        const isValid = validModelIds.has(modelId) || 
                       validModelIds.has(priceModelIdCanonical) ||
                       canonicalModelIds.has(modelId) ||
                       canonicalModelIds.has(priceModelIdCanonical);
        
        if (!isValid) {
          results.push({
            priceId: docSnap.id,
            modelId: modelId,
            reason: 'device_catalog에 모델 없음'
          });
        }
      });
      
      console.log(`🔍 Orphan price 탐지: ${results.length}개`);
    } catch (error) {
      console.error('Orphan 스캔 실패:', error);
    }
    
    return results;
  }
  
  /**
   * 🔍 Duplicate variant 탐지 (동일 modelId+storage+connectivity+version 중복)
   * 
   * 🔑 개선: canonical ID 변환을 적용하여 레거시/canonical 혼재 상황에서도 정확히 탐지
   * 🔑 수정: version(live/draft)도 키에 포함하여 의도적 다중 버전은 중복으로 탐지하지 않음
   */
  static async scanDuplicateVariants(
    useTestCollection: boolean = false
  ): Promise<DuplicateVariantItem[]> {
    const priceCollection = useTestCollection ? TEST_COLLECTIONS.PRICE : PRODUCTION_COLLECTIONS.PRICE;
    const results: DuplicateVariantItem[] = [];
    
    try {
      const snapshot = await getDocs(collection(db, priceCollection));
      
      // 키별로 그룹화 (문서 정보도 함께 저장)
      const variantMap = new Map<string, { priceId: string; updatedAt: Date; version: string }[]>();
      
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data() as UnifiedPriceDocument;
        const modelId = data.modelId || '';
        const storage = data.variant?.storage || '';
        const connectivity = data.variant?.connectivity || 'Wi-Fi';
        const version = data.version || 'live';
        
        // 🔑 개선: modelId를 canonical로 변환하여 키 생성
        // → 레거시 ID와 canonical ID가 같은 모델이면 같은 키로 취급
        const canonicalModelId = extractPureModelId(modelId);
        
        // 🔑 수정: version도 키에 포함! (draft/live는 별개 문서로 취급)
        // 정규화된 키 생성 (canonical modelId + storage + connectivity + version)
        const key = `${canonicalModelId}|${normalizeStorage(storage) || ''}|${normalizeConnectivity(connectivity) || ''}|${version}`;
        
        if (!variantMap.has(key)) {
          variantMap.set(key, []);
        }
        
        // updatedAt 정보도 함께 저장 (삭제 시 최신 데이터 유지용)
        const updatedAt = data.updatedAt instanceof Date 
          ? data.updatedAt 
          : (data.updatedAt as any)?.toDate?.() || new Date(0);
        
        variantMap.get(key)!.push({ 
          priceId: docSnap.id, 
          updatedAt,
          version
        });
      });
      
      // 중복 찾기
      variantMap.forEach((items, key) => {
        if (items.length > 1) {
          const [modelId, storage, connectivity, version] = key.split('|');
          
          // 🔑 개선: updatedAt 기준으로 정렬 (최신이 첫 번째)
          items.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
          
          results.push({
            modelId,
            storage,
            connectivity,
            priceIds: items.map(i => i.priceId),
            count: items.length
          });
        }
      });
      
      console.log(`🔍 Duplicate variant 탐지: ${results.length}개`);
    } catch (error) {
      console.error('Duplicate 스캔 실패:', error);
    }
    
    return results;
  }
  
  /**
   * 🔍 전체 스캔 실행
   */
  static async runFullScan(
    useTestCollection: boolean = false,
    modelIdFilter?: string
  ): Promise<ScanResult> {
    console.log(`🔍 전체 스캔 시작 (테스트 컬렉션: ${useTestCollection})`);
    
    const [nonCanonicalModels, orphanPrices, duplicateVariants] = await Promise.all([
      this.scanNonCanonicalModels(useTestCollection),
      this.scanOrphanPrices(useTestCollection),
      this.scanDuplicateVariants(useTestCollection)
    ]);
    
    // 특정 modelId 필터링
    let filteredNonCanonical = nonCanonicalModels;
    let filteredOrphan = orphanPrices;
    let filteredDuplicate = duplicateVariants;
    
    if (modelIdFilter) {
      const filter = modelIdFilter.toLowerCase();
      filteredNonCanonical = nonCanonicalModels.filter(m => m.id.toLowerCase().includes(filter));
      filteredOrphan = orphanPrices.filter(p => p.modelId.toLowerCase().includes(filter) || p.priceId.toLowerCase().includes(filter));
      filteredDuplicate = duplicateVariants.filter(d => d.modelId.toLowerCase().includes(filter));
    }
    
    // 총 문서 수 계산
    const modelCollection = useTestCollection ? TEST_COLLECTIONS.MODEL : PRODUCTION_COLLECTIONS.MODEL;
    const priceCollection = useTestCollection ? TEST_COLLECTIONS.PRICE : PRODUCTION_COLLECTIONS.PRICE;
    
    const [modelSnap, priceSnap] = await Promise.all([
      getDocs(collection(db, modelCollection)),
      getDocs(collection(db, priceCollection))
    ]);
    
    return {
      nonCanonicalModels: filteredNonCanonical,
      orphanPrices: filteredOrphan,
      duplicateVariants: filteredDuplicate,
      totalModels: modelSnap.size,
      totalPrices: priceSnap.size,
      scannedAt: new Date()
    };
  }
  
  // ========================================
  // 🔑 삭제 기능 (운영 컬렉션)
  // ========================================
  
  /**
   * 🗑️ 비정규 모델 삭제
   * - device_catalog에서 비정규 ID 모델 문서 삭제
   * - 관련 가격 문서도 함께 삭제 (옵션)
   */
  static async deleteNonCanonicalModels(
    modelIds: string[],
    deleteRelatedPrices: boolean = true
  ): Promise<{ deletedModels: number; deletedPrices: number; errors: string[] }> {
    let deletedModels = 0;
    let deletedPrices = 0;
    const errors: string[] = [];
    
    console.log(`🗑️ 비정규 모델 삭제 시작: ${modelIds.length}개`);
    
    for (const modelId of modelIds) {
      try {
        // 1. 관련 가격 삭제 (옵션)
        if (deleteRelatedPrices) {
          const pricesSnapshot = await getDocs(collection(db, PRODUCTION_COLLECTIONS.PRICE));
          const batch = writeBatch(db);
          let priceCount = 0;
          
          pricesSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data() as UnifiedPriceDocument;
            // modelId 직접 비교 및 canonical 비교
            if (data.modelId === modelId || extractPureModelId(data.modelId || '') === extractPureModelId(modelId)) {
              batch.delete(docSnap.ref);
              priceCount++;
            }
          });
          
          if (priceCount > 0) {
            await batch.commit();
            deletedPrices += priceCount;
            console.log(`   📦 ${modelId}: 관련 가격 ${priceCount}개 삭제`);
          }
        }
        
        // 2. 모델 삭제
        await deleteDoc(doc(db, PRODUCTION_COLLECTIONS.MODEL, modelId));
        deletedModels++;
        console.log(`   ✅ ${modelId} 삭제 완료`);
        
      } catch (error) {
        const errMsg = `${modelId}: ${error instanceof Error ? error.message : '알 수 없는 오류'}`;
        errors.push(errMsg);
        console.error(`   ❌ ${errMsg}`);
      }
    }
    
    console.log(`🗑️ 비정규 모델 삭제 완료: 모델 ${deletedModels}개, 가격 ${deletedPrices}개`);
    return { deletedModels, deletedPrices, errors };
  }
  
  /**
   * 🗑️ 고아 가격 삭제
   * - unified_prices에서 모델이 없는 가격 문서 삭제
   */
  static async deleteOrphanPrices(
    priceIds: string[]
  ): Promise<{ deleted: number; errors: string[] }> {
    let deleted = 0;
    const errors: string[] = [];
    
    console.log(`🗑️ 고아 가격 삭제 시작: ${priceIds.length}개`);
    
    // Firestore 배치 최대 500개 제한
    const batchSize = 500;
    for (let i = 0; i < priceIds.length; i += batchSize) {
      const batchIds = priceIds.slice(i, i + batchSize);
      const batch = writeBatch(db);
      
      for (const priceId of batchIds) {
        try {
          batch.delete(doc(db, PRODUCTION_COLLECTIONS.PRICE, priceId));
        } catch (error) {
          errors.push(`${priceId}: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
        }
      }
      
      try {
        await batch.commit();
        deleted += batchIds.length;
        console.log(`   ✅ 배치 삭제 완료: ${batchIds.length}개`);
      } catch (error) {
        errors.push(`배치 삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      }
    }
    
    console.log(`🗑️ 고아 가격 삭제 완료: ${deleted}개`);
    return { deleted, errors };
  }
  
  /**
   * 🗑️ 중복 구성 삭제
   * - 중복된 가격 문서 중 첫 번째(최신 updatedAt)를 제외하고 삭제
   * 
   * 🔑 수정: 
   * - scanDuplicateVariants에서 이미 updatedAt 기준 정렬됨 (최신이 첫 번째)
   * - 첫 번째 문서(최신 데이터)를 유지하고 나머지 삭제
   */
  static async deleteDuplicateVariants(
    duplicates: DuplicateVariantItem[]
  ): Promise<{ deleted: number; kept: { priceId: string; reason: string }[]; errors: string[] }> {
    let deleted = 0;
    const kept: { priceId: string; reason: string }[] = [];
    const errors: string[] = [];
    
    console.log(`🗑️ 중복 구성 삭제 시작: ${duplicates.length}개 그룹`);
    
    for (const dup of duplicates) {
      try {
        // 🔑 수정: 첫 번째가 최신 updatedAt이므로 그대로 유지
        // scanDuplicateVariants에서 이미 updatedAt 내림차순 정렬됨
        const keepId = dup.priceIds[0];
        
        // 유지할 ID를 제외한 나머지 삭제
        const toDelete = dup.priceIds.filter(id => id !== keepId);
        
        if (toDelete.length === 0) {
          console.log(`   ⚠️ ${dup.modelId}/${dup.storage}: 삭제할 문서 없음`);
          continue;
        }
        
        const batch = writeBatch(db);
        for (const priceId of toDelete) {
          batch.delete(doc(db, PRODUCTION_COLLECTIONS.PRICE, priceId));
        }
        
        await batch.commit();
        deleted += toDelete.length;
        kept.push({ priceId: keepId, reason: '최신 updatedAt' });
        console.log(`   ✅ ${dup.modelId}/${dup.storage}: ${toDelete.length}개 삭제 (유지: ${keepId} - 최신)`);
        
      } catch (error) {
        const errMsg = `${dup.modelId}/${dup.storage}: ${error instanceof Error ? error.message : '알 수 없는 오류'}`;
        errors.push(errMsg);
        console.error(`   ❌ ${errMsg}`);
      }
    }
    
    console.log(`🗑️ 중복 구성 삭제 완료: ${deleted}개 삭제, ${kept.length}개 유지`);
    return { deleted, kept, errors };
  }
  
  // ========================================
  // 🔑 Workflow 기능 (테스트 컬렉션 쓰기)
  // ========================================
  
  /**
   * 🚨 운영 컬렉션 쓰기 방지 검사
   */
  private static ensureTestMode(): void {
    if (!this.currentTestRun) {
      throw new Error('🚨 테스트 모드가 아닙니다. startTestRun()을 먼저 호출하세요.');
    }
  }
  
  /**
   * A) 신규 모델 추가 테스트
   */
  static async testAddModel(
    brand: DeviceBrand,
    deviceType: DeviceType,
    series: string,
    modelEn: string,
    modelKo: string,
    storageOptions: string[],
    connectivityOptions?: ConnectivityType[]
  ): Promise<WorkflowTestResult> {
    this.ensureTestMode();
    this.lookupLogs = [];
    
    const testRunId = this.currentTestRun!.testRunId;
    
    // 1. 예상 modelId 생성
    const expectedModelId = this.createCanonicalModelId(brand, deviceType, series, modelEn);
    
    this.addLookupLog({
      step: 1,
      action: 'generate_model_id',
      input: `${brand}/${deviceType}/${series}/${modelEn}`,
      output: expectedModelId,
      success: true,
      isFallback: false
    });
    
    // 2. Canonical 검증
    const isCanonical = isCanonicalModelId(expectedModelId);
    
    this.addLookupLog({
      step: 2,
      action: 'validate_canonical',
      input: expectedModelId,
      output: isCanonical ? 'PASS' : 'FAIL',
      success: isCanonical,
      isFallback: false
    });
    
    if (!isCanonical) {
      return {
        testName: '신규 모델 추가',
        passed: false,
        expectedId: expectedModelId,
        actualId: null,
        details: { reason: 'Generated ID is not canonical' },
        lookupSteps: this.getAndClearLookupLogs(),
        error: '생성된 ID가 canonical 형식이 아닙니다'
      };
    }
    
    // 3. 충돌 검사
    try {
      const existingDoc = await getDoc(doc(db, TEST_COLLECTIONS.MODEL, expectedModelId));
      
      this.addLookupLog({
        step: 3,
        action: 'check_collision',
        input: expectedModelId,
        output: existingDoc.exists() ? 'EXISTS' : 'CLEAR',
        success: !existingDoc.exists(),
        isFallback: false
      });
      
      if (existingDoc.exists()) {
        return {
          testName: '신규 모델 추가',
          passed: false,
          expectedId: expectedModelId,
          actualId: existingDoc.id,
          details: { reason: 'Model already exists' },
          lookupSteps: this.getAndClearLookupLogs(),
          error: '동일 ID의 모델이 이미 존재합니다'
        };
      }
    } catch (error) {
      // 문서가 없으면 정상
    }
    
    // 4. 테스트 컬렉션에 저장
    const modelDoc: Partial<DeviceCatalogDocument> = {
      id: expectedModelId,
      brand,
      deviceType,
      series,
      modelEn,
      modelKo,
      storageOptions,
      connectivityOptions: connectivityOptions || ['Wi-Fi'],
      isActive: true,
      recommended: false,
      priority: 999,
      releaseYear: new Date().getFullYear(),
      image: '/assets/placeholder.png',
      aliases: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      updatedBy: 'ssot-test',
      memo: `테스트 생성: ${testRunId}`,
      // 테스트 마커
      ...(({ isTest: true, testRunId }) as any)
    };
    
    await setDoc(doc(db, TEST_COLLECTIONS.MODEL, expectedModelId), modelDoc);
    
    this.addLookupLog({
      step: 4,
      action: 'save_model',
      input: expectedModelId,
      output: 'SAVED',
      success: true,
      isFallback: false
    });
    
    // 5. 저장 후 검증
    const savedDoc = await getDoc(doc(db, TEST_COLLECTIONS.MODEL, expectedModelId));
    const savedData = savedDoc.data() as DeviceCatalogDocument;
    
    const docIdMatch = savedDoc.id === expectedModelId;
    const brandMatch = savedData.brand === brand;
    const typeMatch = savedData.deviceType === deviceType;
    
    this.addLookupLog({
      step: 5,
      action: 'verify_saved',
      input: expectedModelId,
      output: docIdMatch && brandMatch && typeMatch ? 'PASS' : 'FAIL',
      success: docIdMatch && brandMatch && typeMatch,
      isFallback: false
    });
    
    const passed = docIdMatch && brandMatch && typeMatch;
    
    return {
      testName: '신규 모델 추가',
      passed,
      expectedId: expectedModelId,
      actualId: savedDoc.id,
      details: {
        docIdMatch,
        brandMatch,
        typeMatch,
        savedModelKo: savedData.modelKo,
        savedStorageOptions: savedData.storageOptions
      },
      lookupSteps: this.getAndClearLookupLogs()
    };
  }
  
  /**
   * B) 가격 추가 테스트 (Variant 조합 자동 생성)
   */
  static async testAddPrices(
    modelId: string,
    deviceType: DeviceType,
    brand: DeviceBrand,
    storageOptions: string[],
    connectivityOptions?: ConnectivityType[]
  ): Promise<WorkflowTestResult[]> {
    this.ensureTestMode();
    
    const results: WorkflowTestResult[] = [];
    const testRunId = this.currentTestRun!.testRunId;
    
    // 디바이스 타입별 variant 조합 생성
    let variants: { storage: string; connectivity?: ConnectivityType }[] = [];
    
    if (deviceType === 'wearable') {
      // 웨어러블: base(1개)
      variants = [{ storage: '기본' }];
    } else if (deviceType === 'tablet') {
      // 태블릿: storage × (wifi/cellular)
      const conns = connectivityOptions || ['Wi-Fi', 'Wi-Fi + Cellular'];
      variants = storageOptions.flatMap(storage =>
        conns.map(connectivity => ({ storage, connectivity }))
      );
    } else {
      // 스마트폰: storage만
      variants = storageOptions.map(storage => ({ storage }));
    }
    
    // 각 variant에 대해 가격 추가 테스트
    for (const variant of variants) {
      this.lookupLogs = [];
      
      // 1. 예상 priceId 생성
      const expectedPriceId = this.createCanonicalPriceId(
        modelId,
        variant.storage,
        variant.connectivity
      );
      
      this.addLookupLog({
        step: 1,
        action: 'generate_price_id',
        input: `${modelId}/${variant.storage}/${variant.connectivity || 'Wi-Fi'}`,
        output: expectedPriceId,
        success: true,
        isFallback: false
      });
      
      // 2. 중복 검사
      let isDuplicate = false;
      try {
        const existingDoc = await getDoc(doc(db, TEST_COLLECTIONS.PRICE, expectedPriceId));
        isDuplicate = existingDoc.exists();
        
        this.addLookupLog({
          step: 2,
          action: 'check_duplicate',
          input: expectedPriceId,
          output: isDuplicate ? 'DUPLICATE' : 'CLEAR',
          success: !isDuplicate,
          isFallback: false
        });
      } catch (error) {
        // 문서가 없으면 정상
      }
      
      if (isDuplicate) {
        results.push({
          testName: `가격 추가: ${variant.storage}${variant.connectivity ? '/' + variant.connectivity : ''}`,
          passed: false,
          expectedId: expectedPriceId,
          actualId: expectedPriceId,
          details: { reason: 'Price already exists', variant },
          lookupSteps: this.getAndClearLookupLogs(),
          error: '동일 ID의 가격이 이미 존재합니다'
        });
        continue;
      }
      
      // 3. 저장
      const priceDoc: Partial<UnifiedPriceDocument> = {
        id: expectedPriceId,
        modelId,
        brand,
        deviceType,
        region: 'KR',
        currency: 'KRW',
        variant: {
          storage: variant.storage,
          connectivity: variant.connectivity
        },
        gradePrices: { A: 100000, B: 80000, C: 60000, D: 40000, E: 20000 },
        isActive: true,
        version: 'live',
        createdAt: new Date(),
        updatedAt: new Date(),
        updatedBy: 'ssot-test',
        memo: `테스트 생성: ${testRunId}`,
        // 테스트 마커
        ...(({ isTest: true, testRunId }) as any)
      };
      
      await setDoc(doc(db, TEST_COLLECTIONS.PRICE, expectedPriceId), priceDoc);
      
      this.addLookupLog({
        step: 3,
        action: 'save_price',
        input: expectedPriceId,
        output: 'SAVED',
        success: true,
        isFallback: false
      });
      
      // 4. 저장 후 검증
      const savedDoc = await getDoc(doc(db, TEST_COLLECTIONS.PRICE, expectedPriceId));
      const savedData = savedDoc.data() as UnifiedPriceDocument;
      
      const docIdMatch = savedDoc.id === expectedPriceId;
      const modelIdMatch = savedData.modelId === modelId;
      const storageNormMatch = this.normalizeStorage(savedData.variant?.storage || '') === this.normalizeStorage(variant.storage);
      
      // 5. 실서비스 조회 함수로 "2단계 외 폴백 없음" 검증
      const lookupResult = await this.lookupPriceStrict(modelId, variant.storage, variant.connectivity, true);
      
      this.addLookupLog({
        step: 4,
        action: 'verify_lookup',
        input: `${modelId}/${variant.storage}`,
        output: lookupResult.price ? 'FOUND' : 'NOT_FOUND',
        success: !!lookupResult.price && !lookupResult.hasFallback,
        isFallback: lookupResult.hasFallback
      });
      
      const passed = docIdMatch && modelIdMatch && storageNormMatch && !lookupResult.hasFallback;
      
      results.push({
        testName: `가격 추가: ${variant.storage}${variant.connectivity ? '/' + variant.connectivity : ''}`,
        passed,
        expectedId: expectedPriceId,
        actualId: savedDoc.id,
        details: {
          docIdMatch,
          modelIdMatch,
          storageNormMatch,
          hasFallback: lookupResult.hasFallback,
          savedGradePrices: savedData.gradePrices,
          variant
        },
        lookupSteps: [...this.getAndClearLookupLogs(), ...lookupResult.logs]
      });
    }
    
    return results;
  }
  
  /**
   * C) 가격 수정 테스트
   */
  static async testUpdatePrice(
    priceId: string,
    newGradePrices: GradePriceInfo
  ): Promise<WorkflowTestResult> {
    this.ensureTestMode();
    this.lookupLogs = [];
    
    // 1. 기존 문서 확인
    const existingDoc = await getDoc(doc(db, TEST_COLLECTIONS.PRICE, priceId));
    
    if (!existingDoc.exists()) {
      return {
        testName: '가격 수정',
        passed: false,
        expectedId: priceId,
        actualId: null,
        details: { reason: 'Price not found' },
        lookupSteps: this.getAndClearLookupLogs(),
        error: '수정할 가격을 찾을 수 없습니다'
      };
    }
    
    const oldData = existingDoc.data() as UnifiedPriceDocument;
    const oldModelId = oldData.modelId;
    const oldVariant = oldData.variant;
    
    this.addLookupLog({
      step: 1,
      action: 'load_existing',
      input: priceId,
      output: `modelId: ${oldModelId}`,
      success: true,
      isFallback: false
    });
    
    // 2. gradePrices만 변경, docId/modelId/variant는 유지
    await setDoc(doc(db, TEST_COLLECTIONS.PRICE, priceId), {
      ...oldData,
      gradePrices: newGradePrices,
      updatedAt: new Date(),
      updatedBy: 'ssot-test-update'
    });
    
    this.addLookupLog({
      step: 2,
      action: 'update_price',
      input: priceId,
      output: 'UPDATED',
      success: true,
      isFallback: false
    });
    
    // 3. 검증
    const updatedDoc = await getDoc(doc(db, TEST_COLLECTIONS.PRICE, priceId));
    const updatedData = updatedDoc.data() as UnifiedPriceDocument;
    
    const docIdUnchanged = updatedDoc.id === priceId;
    const modelIdUnchanged = updatedData.modelId === oldModelId;
    const variantUnchanged = JSON.stringify(updatedData.variant) === JSON.stringify(oldVariant);
    const pricesUpdated = JSON.stringify(updatedData.gradePrices) === JSON.stringify(newGradePrices);
    
    this.addLookupLog({
      step: 3,
      action: 'verify_update',
      input: priceId,
      output: docIdUnchanged && modelIdUnchanged && variantUnchanged && pricesUpdated ? 'PASS' : 'FAIL',
      success: docIdUnchanged && modelIdUnchanged && variantUnchanged && pricesUpdated,
      isFallback: false
    });
    
    // 4. 조회 결과 반영 확인
    const lookupResult = await this.lookupPriceStrict(
      oldModelId,
      oldVariant?.storage || '',
      oldVariant?.connectivity,
      true
    );
    
    const lookupPricesMatch = lookupResult.price &&
      lookupResult.price.A === newGradePrices.A &&
      lookupResult.price.B === newGradePrices.B;
    
    const passed = docIdUnchanged && modelIdUnchanged && variantUnchanged && pricesUpdated && lookupPricesMatch;
    
    return {
      testName: '가격 수정',
      passed,
      expectedId: priceId,
      actualId: updatedDoc.id,
      details: {
        docIdUnchanged,
        modelIdUnchanged,
        variantUnchanged,
        pricesUpdated,
        lookupPricesMatch,
        oldPrices: oldData.gradePrices,
        newPrices: updatedData.gradePrices
      },
      lookupSteps: [...this.getAndClearLookupLogs(), ...lookupResult.logs]
    };
  }
  
  /**
   * D) 가격 삭제 테스트
   */
  static async testDeletePrice(
    modelId: string,
    storage: string,
    connectivity?: string
  ): Promise<WorkflowTestResult> {
    this.ensureTestMode();
    this.lookupLogs = [];
    
    // 1. 예상 priceId 확인
    const expectedPriceId = this.createCanonicalPriceId(modelId, storage, connectivity);
    
    this.addLookupLog({
      step: 1,
      action: 'calculate_price_id',
      input: `${modelId}/${storage}/${connectivity || 'Wi-Fi'}`,
      output: expectedPriceId,
      success: true,
      isFallback: false
    });
    
    // 2. 삭제 전 정확히 1개 매칭 확인
    const preDeleteDoc = await getDoc(doc(db, TEST_COLLECTIONS.PRICE, expectedPriceId));
    
    if (!preDeleteDoc.exists()) {
      return {
        testName: '가격 삭제',
        passed: false,
        expectedId: expectedPriceId,
        actualId: null,
        details: { reason: 'Price not found before deletion' },
        lookupSteps: this.getAndClearLookupLogs(),
        error: '삭제할 가격을 찾을 수 없습니다'
      };
    }
    
    this.addLookupLog({
      step: 2,
      action: 'verify_exists',
      input: expectedPriceId,
      output: 'EXISTS',
      success: true,
      isFallback: false
    });
    
    // 3. 삭제
    await deleteDoc(doc(db, TEST_COLLECTIONS.PRICE, expectedPriceId));
    
    this.addLookupLog({
      step: 3,
      action: 'delete_price',
      input: expectedPriceId,
      output: 'DELETED',
      success: true,
      isFallback: false
    });
    
    // 4. 삭제 후 조회 결과 0 확인
    const postDeleteLookup = await this.lookupPriceStrict(modelId, storage, connectivity, true);
    
    this.addLookupLog({
      step: 4,
      action: 'verify_deleted',
      input: expectedPriceId,
      output: postDeleteLookup.price ? 'STILL_EXISTS' : 'DELETED',
      success: !postDeleteLookup.price,
      isFallback: false
    });
    
    // 5. 오탐 매칭 금지 (관대한 폴백 발생 시 FAIL)
    const hasFallbackViolation = postDeleteLookup.hasFallback;
    
    this.addLookupLog({
      step: 5,
      action: 'check_fallback_violation',
      input: 'post_delete_lookup',
      output: hasFallbackViolation ? 'FALLBACK_DETECTED' : 'NO_FALLBACK',
      success: !hasFallbackViolation,
      isFallback: hasFallbackViolation
    });
    
    const passed = !postDeleteLookup.price && !hasFallbackViolation;
    
    return {
      testName: '가격 삭제',
      passed,
      expectedId: expectedPriceId,
      actualId: null,
      details: {
        deletedSuccessfully: !postDeleteLookup.price,
        noFallbackViolation: !hasFallbackViolation
      },
      lookupSteps: [...this.getAndClearLookupLogs(), ...postDeleteLookup.logs]
    };
  }
  
  /**
   * E) Cleanup - testRunId 기준 일괄 삭제
   */
  static async cleanupTestData(testRunId?: string): Promise<{ deletedModels: number; deletedPrices: number }> {
    const targetRunId = testRunId || this.currentTestRun?.testRunId;
    
    if (!targetRunId) {
      throw new Error('testRunId가 필요합니다');
    }
    
    console.log(`🧹 테스트 데이터 정리 시작: ${targetRunId}`);
    
    let deletedPrices = 0;
    let deletedModels = 0;
    
    // 1. 가격 먼저 삭제
    const priceSnapshot = await getDocs(collection(db, TEST_COLLECTIONS.PRICE));
    const priceBatch = writeBatch(db);
    
    priceSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.testRunId === targetRunId || data.isTest === true) {
        priceBatch.delete(docSnap.ref);
        deletedPrices++;
      }
    });
    
    if (deletedPrices > 0) {
      await priceBatch.commit();
    }
    
    // 2. 모델 삭제
    const modelSnapshot = await getDocs(collection(db, TEST_COLLECTIONS.MODEL));
    const modelBatch = writeBatch(db);
    
    modelSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.testRunId === targetRunId || data.isTest === true) {
        modelBatch.delete(docSnap.ref);
        deletedModels++;
      }
    });
    
    if (deletedModels > 0) {
      await modelBatch.commit();
    }
    
    console.log(`🧹 정리 완료: 가격 ${deletedPrices}개, 모델 ${deletedModels}개 삭제`);
    
    // 3. 정합성 0 확인
    const postCleanupScan = await this.runFullScan(true);
    
    if (postCleanupScan.orphanPrices.length > 0 || postCleanupScan.duplicateVariants.length > 0) {
      console.warn('⚠️ 정리 후에도 정합성 문제가 남아있습니다:', postCleanupScan);
    }
    
    return { deletedModels, deletedPrices };
  }
  
  // ========================================
  // 🔑 Playground 기능
  // ========================================
  
  /**
   * Storage/Connectivity 정규화 실험
   */
  static normalizeExperiment(
    inputStorage: string,
    inputConnectivity: string,
    deviceType: DeviceType,
    modelId: string
  ): NormalizeResult & { expectedPriceId: string } {
    const normalizedStorage = this.normalizeStorage(inputStorage, deviceType);
    const normalizedConnectivity = this.normalizeConnectivity(inputConnectivity);
    
    // priceId suffix 생성
    const suffixParts: string[] = [];
    if (normalizedStorage) suffixParts.push(normalizedStorage);
    if (normalizedConnectivity) suffixParts.push(normalizedConnectivity);
    suffixParts.push('kr');
    
    const expectedPriceIdSuffix = suffixParts.join('-');
    const expectedPriceId = this.createCanonicalPriceId(modelId, inputStorage, inputConnectivity);
    
    return {
      inputStorage,
      inputConnectivity,
      normalizedStorage,
      normalizedConnectivity,
      expectedPriceIdSuffix,
      deviceType,
      expectedPriceId
    };
  }
  
  // ========================================
  // 🔑 자동 채점
  // ========================================
  
  /**
   * 자동 채점 (상단 PASS 조건)
   */
  static calculateScorecard(
    scanResult: ScanResult,
    workflowResults: WorkflowTestResult[]
  ): ScorecardResult {
    const details: string[] = [];
    
    // 1. 비정규 ID 0
    const nonCanonicalCount = scanResult.nonCanonicalModels.length;
    if (nonCanonicalCount > 0) {
      details.push(`❌ 비정규 모델 ID: ${nonCanonicalCount}개`);
    } else {
      details.push('✅ 비정규 모델 ID: 0개');
    }
    
    // 2. 고아 가격 0
    const orphanCount = scanResult.orphanPrices.length;
    if (orphanCount > 0) {
      details.push(`❌ 고아 가격: ${orphanCount}개`);
    } else {
      details.push('✅ 고아 가격: 0개');
    }
    
    // 3. 중복 구성 0
    const duplicateCount = scanResult.duplicateVariants.length;
    if (duplicateCount > 0) {
      details.push(`❌ 중복 구성: ${duplicateCount}개`);
    } else {
      details.push('✅ 중복 구성: 0개');
    }
    
    // 4. 예상ID = 실제ID 100%
    const idMatches = workflowResults.filter(r => r.expectedId === r.actualId || (r.actualId === null && r.passed)).length;
    const idMatchRate = workflowResults.length > 0 ? (idMatches / workflowResults.length) * 100 : 100;
    
    if (idMatchRate < 100) {
      details.push(`❌ ID 일치율: ${idMatchRate.toFixed(1)}%`);
    } else {
      details.push('✅ ID 일치율: 100%');
    }
    
    // 5. 조회 단계 로그 2단계 외 존재 시 FAIL
    const allLogs = workflowResults.flatMap(r => r.lookupSteps);
    const noFallbackViolations = !this.hasFallbackViolation(allLogs);
    
    if (!noFallbackViolations) {
      details.push('❌ 폴백 위반 (2단계 외 조회 발생)');
    } else {
      details.push('✅ 폴백 위반 없음');
    }
    
    // 최종 판정
    const passed = nonCanonicalCount === 0 &&
      orphanCount === 0 &&
      duplicateCount === 0 &&
      idMatchRate === 100 &&
      noFallbackViolations;
    
    return {
      passed,
      nonCanonicalCount,
      orphanCount,
      duplicateCount,
      idMatchRate,
      noFallbackViolations,
      details
    };
  }
}

export default SSOTTestService;

